/**
 * MyLegacyBridge Guide — feature catalogue for /guide
 * Status: available | beta | early_access | planned
 */

import {
  STORY_STUDIO_PILLAR_FEATURES,
  STORY_STUDIO_SAFETY,
  STORY_STUDIO_TAGLINE,
} from './storyStudioContent';
import {
  HANDOVER_VAULT_PILLAR_FEATURES,
  HANDOVER_VAULT_SAFETY,
  HANDOVER_VAULT_TAGLINE,
} from './handoverVaultContent';
import {
  MEMORIAL_BRIDGE_PILLAR_FEATURES,
  MEMORIAL_BRIDGE_SAFETY,
  MEMORIAL_BRIDGE_TAGLINE,
} from './memorialBridgeContent';

export const AI_STORY_STUDIO_PILLAR = {
  id: 'ai-story-studio',
  title: 'AI Story Studio',
  tagline: STORY_STUDIO_TAGLINE,
  safety: STORY_STUDIO_SAFETY,
  features: STORY_STUDIO_PILLAR_FEATURES,
};

export const FAMILY_HANDOVER_VAULT_PILLAR = {
  id: 'family-handover-vault',
  title: 'Family Handover Vault',
  tagline: HANDOVER_VAULT_TAGLINE,
  safety: HANDOVER_VAULT_SAFETY,
  features: HANDOVER_VAULT_PILLAR_FEATURES,
};

export const MEMORIAL_BRIDGE_PILLAR = {
  id: 'memorial-bridge',
  title: 'Memorial Bridge',
  tagline: MEMORIAL_BRIDGE_TAGLINE,
  safety: MEMORIAL_BRIDGE_SAFETY,
  features: MEMORIAL_BRIDGE_PILLAR_FEATURES,
};

export const GUIDE_WALKTHROUGH_SLIDES = [
  {
    id: 'archive',
    title: 'Create your archive',
    body: 'Add people, build your family tree, and keep memories in one private place.',
  },
  {
    id: 'stories',
    title: 'Capture stories and voices',
    body: 'Write memories, record voice notes, transcribe them, and polish them into stories.',
  },
  {
    id: 'storybook',
    title: 'Build a Storybook',
    body: 'Guided prompts for a full life story — one chapter at a time.',
  },
  {
    id: 'documents',
    title: 'Preserve documents and priceless possessions',
    body: 'Photos, letters, certificates and keepsakes linked to the people they belong to.',
  },
  {
    id: 'legacy-book',
    title: 'Create a Legacy Book',
    body: 'Export a keepsake book from the stories, people and documents you preserve.',
  },
  {
    id: 'reminders',
    title: 'Set reminders',
    body: 'Birthdays, anniversaries, remembrance days and other family moments.',
  },
  {
    id: 'memorials',
    title: 'Memorial pages',
    body: 'When the time is right — private or public pages with tributes, QR links and memories.',
  },
  {
    id: 'privacy',
    title: 'Privacy and sharing',
    body: 'Private by default. You choose what to share, publish or invite others to see.',
  },
];

export const FEATURE_GUIDE_SECTIONS = [
  {
    id: 'archive-basics',
    title: 'Archive basics',
    intro: 'Your private home for people, relationships and memories.',
    features: [
      { id: 'dashboard', name: 'Dashboard', benefit: 'See all your family archives in one calm place.', howTo: 'Open Dashboard from the sidebar after sign-in.', route: '/dashboard', status: 'available' },
      { id: 'add-yourself', name: 'Add yourself as a person', benefit: 'Link your account to your own profile in the tree.', howTo: 'Open a person profile and choose “This is me”, or add yourself from People.', route: '/dashboard', status: 'available' },
      { id: 'add-people', name: 'Add people', benefit: 'Build your family tree with names, dates and relationships.', howTo: 'Family tree → Add person, or People tab in your archive.', route: null, status: 'available', actionLabel: 'Open your archive', actionHint: 'Choose an archive from the dashboard first.' },
      { id: 'family-tree', name: 'Family tree', benefit: 'Visual chart of generations and connections.', howTo: 'Archive → Family tree tab.', route: null, status: 'available' },
      { id: 'people-list', name: 'People list', benefit: 'Browse everyone in the archive at a glance.', howTo: 'Archive → People tab.', route: null, status: 'available' },
      { id: 'timeline', name: 'Timeline', benefit: 'Dated moments and lifespans across the archive.', howTo: 'Archive → Timeline tab.', route: null, status: 'available' },
      { id: 'archive-settings', name: 'Archive settings', benefit: 'Privacy, exports, memorial defaults and collaboration.', howTo: 'Archive → Archive settings tab.', route: null, status: 'available' },
    ],
  },
  {
    id: 'stories-voice',
    title: 'Stories & voice',
    intro: 'Written memories, voice notes and narrative polish — part of AI Story Studio.',
    features: [
      { id: 'write-memory', name: 'Write a memory', benefit: 'Capture stories in writing, linked to the right person.', howTo: 'Person profile → Stories & voice → Add story.', route: null, status: 'available' },
      { id: 'record-voice', name: 'Record voice note', benefit: 'Preserve voices with optional transcription.', howTo: 'Stories & voice → Record voice memory.', route: null, status: 'available' },
      { id: 'continue-recording', name: 'Continue recording', benefit: 'Add to an existing voice note in stages.', howTo: 'Open a voice story and use Continue recording.', route: null, status: 'available' },
      { id: 'transcribe', name: 'Transcribe to text', benefit: 'Turn audio into editable text when you want it.', howTo: 'Voice story → Transcribe (uses transcription credits).', route: null, status: 'available' },
      { id: 'polish-narrative', name: 'Polish into narrative', benefit: 'Shape raw notes into a readable story.', howTo: 'Writing studio on a story — review before saving.', route: null, status: 'available' },
      { id: 'link-people', name: 'Link people mentioned', benefit: 'Connect stories to relatives in your tree.', howTo: 'When editing a story, link mentioned people.', route: null, status: 'available' },
    ],
  },
  {
    id: 'storybook',
    title: 'Storybook Mode',
    intro: 'Guided life-story chapters at your own pace — the prompt journey inside AI Story Studio.',
    features: [
      { id: 'guided-prompts', name: 'Guided life-story prompts', benefit: 'Sixteen chapters from beginnings to reflections.', howTo: 'Sidebar → Storybook, or person profile → Storybook section.', route: '/storybook', status: 'available' },
      { id: 'chapters', name: 'Chapters', benefit: 'Work through one chapter at a time.', howTo: 'Inside a storybook workspace.', route: '/storybook', status: 'available' },
      { id: 'interview-mode', name: 'Interview Mode', benefit: 'One question at a time — ideal for recording answers.', howTo: 'Open a storybook → Interview Mode.', route: '/storybook', status: 'available' },
      { id: 'voice-answers', name: 'Voice answers', benefit: 'Answer prompts by speaking; kept in your archive.', howTo: 'Record inside Interview Mode or story prompts.', route: '/storybook', status: 'available' },
      { id: 'ai-chapter-draft', name: 'AI chapter draft', benefit: 'Optional AI help drafting a chapter from your answers — review before it is final.', howTo: 'Storybook workspace sidebar → Create chapter draft after 3+ completed answers in a chapter.', route: '/storybook', status: 'available' },
      { id: 'storybook-export', name: 'Storybook PDF export', benefit: 'Export a storybook as its own keepsake.', howTo: 'Coming to Preview & export.', route: null, status: 'planned' },
    ],
  },
  {
    id: 'media-possessions',
    title: 'Photos, documents & priceless possessions',
    intro: 'Keepsakes linked to people — included in your Legacy Book.',
    features: [
      { id: 'preserve-documents', name: 'Preserve documents', benefit: 'Letters, certificates and scans with context.', howTo: 'Person → Documents → Preserve a document.', route: null, status: 'available' },
      { id: 'upload-photos', name: 'Upload photos', benefit: 'Attach photographs to the people they belong to.', howTo: 'Documents tab or profile photo on overview.', route: null, status: 'available' },
      { id: 'priceless', name: 'Priceless possessions', benefit: 'Objects with stories — heirlooms, gifts, mementos.', howTo: 'Person overview or People, places & things in archive.', route: null, status: 'available' },
      { id: 'link-items', name: 'Link items to people', benefit: 'Every item stays with the right life story.', howTo: 'Choose a person when adding documents or possessions.', route: null, status: 'available' },
      { id: 'legacy-book-include', name: 'Include in Legacy Book', benefit: 'Completed profiles and media flow into export.', howTo: 'Archive settings → Exports → Keepsake PDF.', route: null, status: 'available' },
    ],
  },
  {
    id: 'reminders',
    title: 'Reminders',
    intro: 'Keep important family dates close — privately.',
    features: [
      { id: 'birthday', name: 'Birthday reminders', benefit: 'Never miss a family birthday.', howTo: 'Reminders → Add reminder → Birthday.', route: '/reminders', status: 'available' },
      { id: 'remembrance', name: 'Remembrance days', benefit: 'Quiet prompts on anniversaries of loss.', howTo: 'Reminders → Remembrance type.', route: '/reminders', status: 'available' },
      { id: 'anniversaries', name: 'Anniversaries', benefit: 'Wedding and partnership dates preserved.', howTo: 'Reminders → Add custom or anniversary.', route: '/reminders', status: 'available' },
      { id: 'custom-dates', name: 'Custom dates', benefit: 'Naming days, reunions, or any family moment.', howTo: 'Reminders → Custom date.', route: '/reminders', status: 'available' },
      { id: 'digital-cards', name: 'Digital tribute cards', benefit: 'Shareable cards for special days.', howTo: 'Reminders → Cards tab (preview cards from saved reminders).', route: '/reminders', status: 'available' },
    ],
  },
  {
    id: 'exports',
    title: 'Legacy Book exports',
    intro: 'Turn your archive into printable keepsakes and backups.',
    features: [
      { id: 'family-book', name: 'Family Legacy Book', benefit: 'Full archive keepsake PDF.', howTo: 'Archive settings → Exports → Keepsake PDF.', route: null, status: 'available' },
      { id: 'person-book', name: 'Person Legacy Book', benefit: 'Focus on one person’s profile and memories.', howTo: 'Person overview → Export, or archive export with person focus.', route: null, status: 'available' },
      { id: 'review-pdf', name: 'Draft Review PDF', benefit: 'Readiness notes before final export — not for sharing.', howTo: 'Exports → Review draft PDF.', route: null, status: 'available' },
      { id: 'storybook-pdf', name: 'Storybook PDF', benefit: 'Export a completed storybook.', howTo: 'Planned in Storybook and export flows.', route: null, status: 'planned' },
      { id: 'json-backup', name: 'Data backup (JSON)', benefit: 'Full archive data export anytime.', howTo: 'Archive settings → Exports → Download JSON.', route: null, status: 'available' },
      { id: 'ai-book-prep', name: 'AI-assisted book preparation', benefit: 'Review suggestions before final keepsake export.', howTo: 'Preview & export → AI Legacy Book panel.', route: null, status: 'available' },
    ],
  },
  {
    id: 'memorials',
    title: 'Memorials',
    intro: 'Optional, respectful tribute pages — private until you publish.',
    features: [
      { id: 'memorial-workspace', name: 'Memorial workspace', benefit: 'Prepare photos, tributes and settings privately.', howTo: 'Person profile → Memorial, or Memorials page.', route: '/memorials', status: 'available' },
      { id: 'public-memorial', name: 'Public or private memorial page', benefit: 'Share only when your family chooses.', howTo: 'Memorial workspace → Publish when ready.', route: '/memorials', status: 'available' },
      { id: 'tribute-wall', name: 'Tribute wall', benefit: 'Moderated messages from visitors.', howTo: 'Enable on published memorial pages.', route: '/memorials', status: 'available' },
      { id: 'qr-code', name: 'QR code', benefit: 'Printable link for services and keepsakes.', howTo: 'Published memorial → QR panel.', route: '/memorials', status: 'available' },
      { id: 'keepsakes', name: 'Memorial keepsakes', benefit: 'Physical QR cards and plaques.', howTo: 'Memorial workspace after publishing.', route: '/memorials', status: 'available' },
      { id: 'support-link', name: 'External support link', benefit: 'Optional donation link chosen by the family.', howTo: 'Memorial workspace → Support section.', route: '/memorials', status: 'available' },
    ],
  },
  {
    id: 'privacy-trust',
    title: 'Privacy & trust',
    intro: 'Private by default — you control every share.',
    features: [
      { id: 'private-default', name: 'Private by default', benefit: 'Nothing is public until you publish.', howTo: 'Archive settings → Privacy.', route: null, status: 'available' },
      { id: 'invite-family', name: 'Invite family', benefit: 'Collaborate with view or edit access.', howTo: 'Archive → Invite family.', route: null, status: 'available' },
      { id: 'verification', name: 'Connection verification', benefit: 'Verify family links when ready.', howTo: 'People & connections in archive settings.', route: null, status: 'available' },
      { id: 'visibility', name: 'Archive visibility', benefit: 'Opt-in surname discovery only if you choose.', howTo: 'Archive settings → Privacy.', route: null, status: 'available' },
      { id: 'dna-privacy', name: 'DNA beta privacy', benefit: 'Genetic files stay in your archive.', howTo: 'DNA section — consent before upload.', route: '/dna', status: 'beta' },
      { id: 'export-anytime', name: 'Export anytime', benefit: 'PDF and JSON backups on your timeline.', howTo: 'Archive settings → Exports.', route: null, status: 'available' },
    ],
  },
  {
    id: 'dna',
    title: 'DNA beta',
    intro: 'Optional genetic files for family history — not medical reports.',
    features: [
      { id: 'upload-dna', name: 'Upload raw DNA file', benefit: 'Use files from testing services you already used.', howTo: 'DNA management page.', route: '/dna', status: 'beta' },
      { id: 'storage-consent', name: 'Storage consent', benefit: 'Explicit consent before files are stored.', howTo: 'Upload flow on DNA page.', route: '/dna', status: 'beta' },
      { id: 'matching-consent', name: 'Matching consent', benefit: 'Opt in to possible relative connections.', howTo: 'DNA settings after upload.', route: '/dna', status: 'beta' },
      { id: 'early-matching', name: 'Early access matching', benefit: 'Explore surname and cluster hints.', howTo: 'DNA page — beta features labelled clearly.', route: '/dna', status: 'early_access' },
      { id: 'no-medical', name: 'No medical or ethnicity reports', benefit: 'Family history exploration only.', howTo: 'See FAQ for scope.', route: '/faq', status: 'available' },
    ],
  },
  {
    id: 'community',
    title: 'Community & organisation archives',
    intro: 'Planned for organisations preserving shared heritage.',
    features: [
      { id: 'org-archive', name: 'Organisation archive', benefit: 'Multi-family or institutional archives.', howTo: 'Early access — contact support when available.', route: null, status: 'planned' },
      { id: 'family-spaces', name: 'Family Spaces', benefit: 'Shared spaces across related archives.', howTo: 'Family Spaces (preview).', route: '/family-spaces', status: 'early_access' },
    ],
  },
];

export const STATUS_LABELS = {
  available: 'Available',
  beta: 'Beta',
  early_access: 'Early access',
  planned: 'Planned',
};

export const PLAN_BADGE_LABEL = 'Available for testing';
